import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, ArrowRight, ArrowDown, ScanLine, GitCompareArrows, Handshake,
  Boxes, Newspaper, Package, Scissors, Truck, Factory, Store, Building2, Printer,
  UtensilsCrossed, Layers, Minus, Equal,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { NetbackCalculator } from "@/components/NetbackCalculator";
import { BRAND, IMAGES } from "@/lib/site";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative py-20 lg:py-32 px-4 sm:px-6 lg:px-8 ${className}`}>
    <div className="max-w-[1400px] mx-auto">{children}</div>
  </section>
);

const Kicker = ({ n, children }) => (
  <div className="flex items-center gap-3 mb-6">
    {n && <span className="font-mono text-dc-accent text-sm">{n}</span>}
    <span className="dc-label">{children}</span>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const overlayO = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Baled recovered cardboard and paper" className="w-full h-[120%] object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayO }} className="absolute inset-0 z-0 bg-dc-bg" />
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-dc-bg/90 via-dc-bg/40 to-transparent" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-dc-bg via-transparent to-dc-bg/30" />
        <div className="absolute inset-0 z-0 dc-grid-lines opacity-40" />

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 pt-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-8">
            <span className="dc-label">{BRAND.tagline}</span>
          </motion.div>

          <h1 className="font-display font-semibold uppercase tracking-tighter text-white leading-[0.92] text-[13vw] sm:text-7xl lg:text-8xl xl:text-9xl">
            <MaskedLine delay={0.15}>Your Waste</MaskedLine>
            <MaskedLine delay={0.3}>Has a Market.</MaskedLine>
            <MaskedLine delay={0.45}><span className="text-dc-accent">We Find It.</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-8 max-w-2xl text-base lg:text-lg text-dc-text2 leading-relaxed"
          >
            We connect UK businesses generating bulk cardboard, paper and packaging waste with suitable recycling
            markets, authorised logistics providers and recovered-material off-takers — turning recyclable waste
            streams into commercial opportunities.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button data-testid="hero-cta-assessment" onClick={() => navigate("/contact")} className="inline-flex items-center justify-center gap-2 bg-dc-accent text-dc-bg font-semibold px-7 py-4 hover:bg-dc-accentHover transition-colors">
              Request a Waste Value Assessment <ArrowUpRight size={18} />
            </button>
            <button data-testid="hero-cta-partner" onClick={() => navigate("/partners")} className="inline-flex items-center justify-center gap-2 border border-dc-borderHi text-white px-7 py-4 hover:border-dc-accent hover:text-dc-accent transition-colors">
              Become a Trading Partner
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-dc-text2"
          >
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-dc-accent" /> Registered Upper Tier Waste Broker · Environment Agency</span>
            <span className="font-mono text-dc-accent">{BRAND.eaReg}</span>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="hidden lg:flex absolute bottom-10 right-8 z-10 items-center gap-2 text-dc-text2 text-xs">
          <ArrowDown size={14} className="animate-bounce" /> Scroll
        </motion.div>
      </section>

      {/* TRUST MARQUEE */}
      <div className="border-y border-dc-border bg-dc-bg py-5">
        <Marquee speed={30} gradient gradientColor="#07090E" gradientWidth={80}>
          {["Environment Agency Registered", "Bulk Commercial Waste", "Recovered Fibre", "Independent Brokerage", "UK-Wide Network", "Duty of Care Focused"].map((t, i) => (
            <span key={i} className="mx-10 font-display uppercase tracking-wide text-dc-text2/60 text-lg">{t} <span className="text-dc-accent/60 mx-2">/</span></span>
          ))}
        </Marquee>
      </div>

      {/* RECOVER MORE VALUE */}
      <Section id="businesses">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-8">
            <Kicker n="01">Recover More Value</Kicker>
            <Reveal>
              <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95]">
                Stop Treating Recyclable Material as a Cost Centre
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <p className="text-dc-text2 leading-relaxed">
                For many high-volume businesses, cardboard and paper are more than disposal streams. When properly
                segregated, presented and routed, recovered fibre can have measurable commodity value.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-dc-border">
          {[
            { icon: ScanLine, t: "Assess", d: "We analyse volumes, grades, contamination, presentation, current collection arrangements and existing commercial terms." },
            { icon: GitCompareArrows, t: "Optimise", d: "We compare potential recycling destinations, logistics configurations and recovered-material markets." },
            { icon: Handshake, t: "Broker", d: "We coordinate suitable commercial relationships between producers, authorised carriers and receiving facilities." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className="group border-r border-b border-dc-border p-8 lg:p-10 h-full hover:bg-dc-surface transition-colors duration-300 relative">
                <div className="absolute top-0 left-0 h-[2px] w-0 bg-dc-accent group-hover:w-full transition-all duration-500" />
                <c.icon className="text-dc-accent mb-6" size={30} strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3">{c.t}</h3>
                <p className="text-dc-text2 text-sm leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <button data-testid="benchmark-cta" onClick={() => navigate("/contact")} className="mt-10 inline-flex items-center gap-2 text-white hover:text-dc-accent transition-colors group">
            <span className="border-b border-dc-borderHi group-hover:border-dc-accent pb-1">Benchmark My Current Arrangement</span> <ArrowRight size={16} />
          </button>
        </Reveal>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how-it-works" className="bg-dc-surface/40 border-y border-dc-border">
        <Kicker n="02">How It Works</Kicker>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-16">
          <Reveal><h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95]">We Coordinate the Transaction — Not the Truck</h2></Reveal>
          <Reveal delay={0.1}><p className="text-dc-text2 leading-relaxed">DES-CARTA LTD operates an asset-light brokerage model. We own no vehicles, skips, yards or processing facilities. Authorised third-party carriers move material directly between producer sites and receiving facilities.</p></Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-dc-border border border-dc-border">
          {[
            { n: "01", t: "Material Assessment", d: "We understand what you produce, where, how much and in what condition." },
            { n: "02", t: "Market & Route Analysis", d: "We assess suitable recycling outlets, material specifications and logistics." },
            { n: "03", t: "Commercial Proposal", d: "We establish the proposed route, collection model and commercial terms." },
            { n: "04", t: "Logistics Coordination", d: "Authorised third-party carriers transport material directly between sites." },
            { n: "05", t: "Recovery & Reporting", d: "Movements and documentation are managed through a Duty of Care workflow." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="bg-dc-bg p-6 lg:p-7 h-full">
                <div className="font-display text-3xl text-dc-accent mb-4">{s.n}</div>
                <h3 className="font-medium text-white mb-2 text-sm uppercase tracking-wide">{s.t}</h3>
                <p className="text-dc-text2 text-xs leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* MATERIALS */}
      <Section id="materials">
        <Kicker n="03">Bulk Material Streams</Kicker>
        <Reveal><h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95] mb-4">Material Streams We Broker</h2></Reveal>
        <Reveal delay={0.1}><p className="text-dc-text2 max-w-2xl mb-14 leading-relaxed">Value depends upon grade, quality, contamination, volume, presentation, location and prevailing market conditions. Not every material carries positive value.</p></Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-dc-border border border-dc-border">
          {[
            { icon: Boxes, t: "OCC / Cardboard", d: "Commercial quantities of segregated corrugated cardboard from warehouses, distribution, manufacturing and retail operations." },
            { icon: Newspaper, t: "Recovered Paper", d: "Suitable segregated commercial paper grades for recycling and reprocessing markets." },
            { icon: Package, t: "Packaging Waste", d: "Commercial paper/card packaging streams requiring compliant, commercially efficient recovery routes." },
            { icon: Scissors, t: "Production & Conversion Waste", d: "Suitable paper/card offcuts and production streams from printers, converters and manufacturers." },
          ].map((m, i) => (
            <Reveal key={m.t} delay={i * 0.08}>
              <div className="group bg-dc-bg p-8 lg:p-10 h-full hover:bg-dc-surface transition-colors">
                <m.icon className="text-dc-accent mb-6" size={30} strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3">{m.t}</h3>
                <p className="text-dc-text2 text-sm leading-relaxed">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* COMMERCIAL MODEL + CALCULATOR */}
      <Section className="bg-dc-surface/40 border-y border-dc-border">
        <Kicker n="04">The Commercial Model</Kicker>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-14">
          <Reveal>
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95] mb-6">Commercial Recycling Built Around Net Value</h2>
            <div className="space-y-2 font-mono text-sm">
              {[["Recovered Material Market Value", ""], ["Collection & Logistics", "minus"], ["Applicable Processing / Handling", "minus"]].map(([l, op]) => (
                <div key={l} className="flex items-center gap-3 text-dc-text2">
                  {op === "minus" ? <Minus size={14} className="text-dc-accent" /> : <span className="w-3.5" />}<span>{l}</span>
                </div>
              ))}
              <div className="flex items-center gap-3 text-dc-accent border-t border-dc-border pt-3 mt-3">
                <Equal size={14} /><span className="text-white font-semibold">Net Recovered Value</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-dc-text2 leading-relaxed mb-6">We evaluate both sides of the recovered-material equation, then structure a commercial arrangement appropriate to the material and customer. Potential arrangements may include:</p>
            <div className="grid grid-cols-2 gap-3">
              {["Commodity rebates", "Fixed-price arrangements", "Indexed pricing", "Shared-uplift structures", "Managed brokerage agreements"].map((a) => (
                <div key={a} className="border border-dc-border p-3 text-sm text-white">{a}</div>
              ))}
            </div>
            <p className="text-[11px] text-dc-muted mt-6 leading-relaxed">Commercial terms are subject to material specification, volume, logistics, destination and prevailing market conditions.</p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mb-6"><span className="dc-label">Example Commercial Scenario — Netback Calculator</span></div>
          <NetbackCalculator />
        </Reveal>
      </Section>

      {/* WHO WE WORK WITH */}
      <Section>
        <Kicker n="05">Who We Work With</Kicker>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center mb-14">
          <Reveal className="lg:col-span-2"><h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95]">Designed for Organisations Generating Commercial Quantities</h2></Reveal>
          <Reveal delay={0.1}>
            <button data-testid="discuss-stream-cta" onClick={() => navigate("/contact")} className="inline-flex items-center gap-2 border border-dc-borderHi text-white px-6 py-3.5 hover:border-dc-accent hover:text-dc-accent transition-colors">Discuss Your Waste Stream <ArrowUpRight size={16} /></button>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-dc-border border border-dc-border">
          {[
            { icon: Truck, t: "Distribution & Fulfilment" }, { icon: Factory, t: "Manufacturing" },
            { icon: Store, t: "Retail & Wholesale" }, { icon: Printer, t: "Commercial Printing" },
            { icon: Package, t: "Packaging & Converting" }, { icon: UtensilsCrossed, t: "Food & Beverage Distribution" },
            { icon: Building2, t: "Industrial Operations" }, { icon: Layers, t: "Multi-Site Businesses" },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.05}>
              <div className="group bg-dc-bg p-6 lg:p-8 h-full flex flex-col gap-4 hover:bg-dc-surface transition-colors">
                <c.icon className="text-dc-text2 group-hover:text-dc-accent transition-colors" size={26} strokeWidth={1.5} />
                <span className="text-sm text-white leading-snug">{c.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SHARED VALUE + IMAGE */}
      <Section className="bg-dc-surface/40 border-y border-dc-border">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden border border-dc-border">
              <img src={IMAGES.logistics} alt="Loading bay logistics" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-dc-bg/20" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Kicker>Shared Value</Kicker>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[0.95] mb-6">Our Interests Can Be Aligned With Yours</h2>
            <p className="text-dc-text2 leading-relaxed mb-4">Where appropriate, we can benchmark an organisation's existing recovered-material arrangement and structure our remuneration around part of the incremental commercial value we create.</p>
            <p className="text-dc-text2 leading-relaxed mb-8">That means our incentive is simple: identify a stronger commercial outcome for the client.</p>
            <button data-testid="shared-value-cta" onClick={() => navigate("/contact")} className="inline-flex items-center gap-2 bg-dc-accent text-dc-bg font-semibold px-6 py-3.5 hover:bg-dc-accentHover transition-colors">Request a Benchmark Assessment <ArrowRight size={16} /></button>
          </Reveal>
        </div>
      </Section>

      {/* PARTNER TEASER */}
      <Section>
        <Kicker n="06">Partner Network</Kicker>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-14">
          <Reveal><h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95]">A Brokerage Network Built for Volume</h2></Reveal>
          <Reveal delay={0.1}><p className="text-dc-text2 leading-relaxed">We develop relationships across the recovered-material supply chain — reprocessors, exporters, receiving facilities and hauliers.</p></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-dc-border border border-dc-border">
          {[
            { t: "Reprocessors & Paper Mills", d: "Access suitable UK recovered-fibre supply opportunities.", cta: "Discuss Fibre Supply" },
            { t: "Exporters & Material Traders", d: "Discuss eligible recovered-paper and cardboard streams.", cta: "Become an Off-Take Partner" },
            { t: "Waste & Recycling Facilities", d: "Explore regional receiving and processing opportunities.", cta: "Join Our Receiving Network" },
            { t: "Hauliers", d: "Work with us on recurring industrial recycling movements.", cta: "Join Our Logistics Network" },
          ].map((p, i) => (
            <Reveal key={p.t} delay={i * 0.08}>
              <div className="group bg-dc-bg p-8 h-full flex flex-col hover:bg-dc-surface transition-colors">
                <h3 className="font-display text-xl mb-3">{p.t}</h3>
                <p className="text-dc-text2 text-sm leading-relaxed mb-6 flex-1">{p.d}</p>
                <button onClick={() => navigate("/partners")} className="inline-flex items-center gap-2 text-dc-accent text-sm hover:gap-3 transition-all">{p.cta} <ArrowRight size={15} /></button>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CLOSING CTA */}
      <Section className="border-t border-dc-border">
        <div className="grid grid-cols-1 lg:grid-cols-2 border border-dc-border">
          <div className="p-10 lg:p-16 border-b lg:border-b-0 lg:border-r border-dc-border group hover:bg-dc-surface transition-colors">
            <div className="dc-label mb-4">I have material</div>
            <h3 className="font-display text-3xl lg:text-4xl mb-4">Request a Waste Value Assessment</h3>
            <p className="text-dc-text2 mb-8 leading-relaxed">Benchmark your current arrangement and discover the strongest commercially viable route for your recovered material.</p>
            <button data-testid="closing-cta-assessment" onClick={() => navigate("/contact")} className="inline-flex items-center gap-2 bg-dc-accent text-dc-bg font-semibold px-6 py-3.5 hover:bg-dc-accentHover transition-colors">Assess My Waste Stream <ArrowUpRight size={16} /></button>
          </div>
          <div className="p-10 lg:p-16 group hover:bg-dc-surface transition-colors">
            <div className="dc-label mb-4">I buy / move / process material</div>
            <h3 className="font-display text-3xl lg:text-4xl mb-4">Become a Trading Partner</h3>
            <p className="text-dc-text2 mb-8 leading-relaxed">Join a brokerage network built for volume — recovered fibre supply, off-take and recurring industrial movements.</p>
            <button data-testid="closing-cta-partner" onClick={() => navigate("/partners")} className="inline-flex items-center gap-2 border border-dc-borderHi text-white px-6 py-3.5 hover:border-dc-accent hover:text-dc-accent transition-colors">Explore Partnership <ArrowUpRight size={16} /></button>
          </div>
        </div>
      </Section>
    </>
  );
}
